import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { 
  RefreshCw, 
  TrendingUp, 
  TrendingDown,
  ArrowRight,
  Zap,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_V2 = `${BACKEND_URL}/api/v2`;

const PriceCompare = ({ activePairs = ['BTC-USD', 'ETH-USD', 'SOL-USD'] }) => {
  const [priceData, setPriceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const formatPrice = (price, pair) => {
    if (!price || price === 0) return '-';
    
    if (price < 0.0001) {
      return `$${price.toFixed(8)}`;
    } else if (price < 0.01) {
      return `$${price.toFixed(6)}`;
    } else if (price < 1) {
      return `$${price.toFixed(4)}`;
    } else if (price < 100) {
      return `$${price.toFixed(2)}`;
    } else {
      return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  const fetchPrices = useCallback(async () => {
    try {
      const pairsParam = activePairs.join(',');
      const response = await axios.get(`${API_V2}/market/multi-exchange-prices`, {
        params: { pairs: pairsParam }
      });
      
      if (response.data.success) {
        setPriceData(response.data);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Failed to fetch multi-exchange prices:', error);
    } finally {
      setLoading(false);
    }
  }, [activePairs]);

  useEffect(() => {
    fetchPrices();
    
    // Auto-refresh every 5 seconds
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchPrices, 8000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchPrices, autoRefresh]);

  const exchanges = ['coinbase', 'binance', 'kraken', 'crypto_com'];
  const exchangeNames = {
    coinbase: 'Coinbase',
    binance: 'Binance',
    kraken: 'Kraken',
    crypto_com: 'Crypto.com'
  };
  const exchangeColors = {
    coinbase: '#0052FF',
    binance: '#F3BA2F',
    kraken: '#5741D9',
    crypto_com: '#002D74'
  };

  if (loading && !priceData) {
    return (
      <Card className="matrix-card rounded-none border border-[#003B00]">
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <RefreshCw className="h-5 w-5 text-[#00FF41] animate-spin mr-2" />
            <span className="text-[#008F11]">Loading prices from exchanges...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="matrix-card rounded-none border border-[#003B00]">
      <CardHeader className="border-b border-[#003B00] pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-[#00FF41]" />
            <CardTitle className="text-[#00FF41] text-sm tracking-widest">
              MULTI-EXCHANGE PRICE COMPARISON
            </CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Badge 
              variant="outline" 
              className={`text-[10px] cursor-pointer ${autoRefresh ? 'border-[#00FF41] text-[#00FF41]' : 'border-[#003B00] text-[#008F11]'}`}
              onClick={() => setAutoRefresh(!autoRefresh)}
              data-testid="auto-refresh-toggle"
            >
              {autoRefresh ? 'LIVE' : 'PAUSED'}
            </Badge>
            <Button
              onClick={fetchPrices}
              className="matrix-btn rounded-none h-7 text-xs"
              disabled={loading}
              data-testid="refresh-prices-btn"
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
              REFRESH
            </Button>
          </div>
        </div>
        {lastUpdate && (
          <p className="text-[#006400] text-[10px] mt-1">
            Last updated: {lastUpdate.toLocaleTimeString()}
          </p>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {/* Best Arbitrage Alert */}
        {priceData?.best_arbitrage?.has_opportunity && (
          <div className="p-3 bg-gradient-to-r from-yellow-900/30 to-yellow-800/10 border-b border-yellow-600/30">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-yellow-400" />
              <span className="text-yellow-400 text-xs font-bold tracking-wider">
                ARBITRAGE OPPORTUNITY DETECTED
              </span>
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className="text-[#008F11]">{priceData.best_arbitrage.pair}</span>
              <span className="text-green-400">
                BUY @ {exchangeNames[priceData.best_arbitrage.buy_exchange]} ({formatPrice(priceData.best_arbitrage.buy_price)})
              </span>
              <ArrowRight className="h-3 w-3 text-yellow-400" />
              <span className="text-red-400">
                SELL @ {exchangeNames[priceData.best_arbitrage.sell_exchange]} ({formatPrice(priceData.best_arbitrage.sell_price)})
              </span>
              <Badge className="bg-yellow-900 text-yellow-200 border-yellow-600 text-[10px]">
                +{priceData.best_arbitrage.spread_percent.toFixed(2)}%
              </Badge>
            </div>
          </div>
        )}

        {/* Price Table */}
        <ScrollArea className="max-h-[400px]">
          <table className="w-full">
            <thead className="bg-[#001a00] sticky top-0">
              <tr>
                <th className="text-left text-[#00FF41] text-xs p-3 font-mono tracking-wider">
                  PAIR
                </th>
                {exchanges.map(ex => (
                  <th 
                    key={ex} 
                    className="text-right text-xs p-3 font-mono tracking-wider"
                    style={{ color: exchangeColors[ex] }}
                  >
                    {exchangeNames[ex].toUpperCase()}
                  </th>
                ))}
                <th className="text-right text-[#008F11] text-xs p-3 font-mono tracking-wider">
                  SPREAD
                </th>
              </tr>
            </thead>
            <tbody>
              {priceData && Object.entries(priceData.pairs).map(([pair, data]) => {
                const arb = data.arbitrage;
                const prices = data.prices;
                
                // Find min/max for highlighting
                const validPrices = Object.entries(prices)
                  .filter(([_, v]) => v.price !== null)
                  .map(([k, v]) => ({ exchange: k, price: v.price }));
                
                const minPrice = validPrices.length > 0 
                  ? Math.min(...validPrices.map(p => p.price)) 
                  : null;
                const maxPrice = validPrices.length > 0 
                  ? Math.max(...validPrices.map(p => p.price)) 
                  : null;
                
                return (
                  <tr key={pair} className="border-b border-[#003B00]/50 hover:bg-[#001a00]/50">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="text-[#00FF41] font-bold text-sm">
                          {pair.split('-')[0]}
                        </span>
                        {arb.has_opportunity && (
                          <Zap className="h-3 w-3 text-yellow-400" />
                        )}
                      </div>
                    </td>
                    {exchanges.map(ex => {
                      const priceInfo = prices[ex];
                      const price = priceInfo?.price;
                      const isMin = price !== null && price === minPrice && validPrices.length > 1;
                      const isMax = price !== null && price === maxPrice && validPrices.length > 1;
                      
                      return (
                        <td 
                          key={ex} 
                          className={`text-right p-3 font-mono text-sm ${
                            price === null 
                              ? 'text-[#003B00]'
                              : isMin 
                                ? 'text-green-400 font-bold' 
                                : isMax 
                                  ? 'text-red-400 font-bold'
                                  : 'text-[#008F11]'
                          }`}
                        >
                          {price !== null ? (
                            <div className="flex items-center justify-end gap-1">
                              {isMin && <TrendingDown className="h-3 w-3" />}
                              {isMax && <TrendingUp className="h-3 w-3" />}
                              {formatPrice(price, pair)}
                            </div>
                          ) : (
                            <span className="opacity-50">-</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="text-right p-3">
                      {arb.spread_percent > 0 ? (
                        <Badge 
                          className={`text-[10px] ${
                            arb.has_opportunity 
                              ? 'bg-yellow-900 text-yellow-200 border-yellow-600' 
                              : 'bg-[#001a00] text-[#008F11] border-[#003B00]'
                          }`}
                        >
                          {arb.spread_percent.toFixed(2)}%
                        </Badge>
                      ) : (
                        <span className="text-[#003B00] text-xs">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollArea>

        {/* Legend */}
        <div className="p-3 border-t border-[#003B00] bg-[#001500]">
          <div className="flex items-center justify-between text-[10px]">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <TrendingDown className="h-3 w-3 text-green-400" />
                <span className="text-green-400">LOWEST (BUY)</span>
              </span>
              <span className="flex items-center gap-1">
                <TrendingUp className="h-3 w-3 text-red-400" />
                <span className="text-red-400">HIGHEST (SELL)</span>
              </span>
              <span className="flex items-center gap-1">
                <Zap className="h-3 w-3 text-yellow-400" />
                <span className="text-yellow-400">ARBITRAGE {'>'}0.5%</span>
              </span>
            </div>
            <span className="text-[#006400]">
              Spread = % difference between lowest & highest
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PriceCompare;
